import { useCallback, useMemo, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import { intersectionBy } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { TimeSignal } from 'graphql/types';
import { useMessage } from 'modules/intl';
import { useCollatedTimesheetLabels } from 'modules/timesheet-labels/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { mapTimesheetLabel } from 'modules/common/hooks/labelMapUtils';
import { useCollatedCustomLabels } from 'modules/customLabels/hooks/useCollatedCustomLabels';
import { useFeatureFlag, useLabels } from 'modules/common/hooks';
import { OFFSET_ENTRY, SPLIT_NOTE_FIELDS } from '../types';

import {
  SplitNoteFormData,
  SplitEntryFormData,
  splitNoteSchema
} from '../splitNoteSchema';

export type SplitHandlers = {
  splitEntryHandler: (id: string) => void;
  removeSplitHandler: (id: string) => void;
  fillOffsetHandler: () => void;
  adjustEntriesHandler: () => void;
};
type ReturnType = SplitHandlers & {
  formMethods: UseFormReturn<SplitNoteFormData>;
  splitEntries: SplitEntryFormData[];
  labelLoading: boolean;
  showStartEndTime: boolean;
};

export const useSplitNoteForm = (note: TimeSignal): ReturnType => {
  const { date } = note;
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const noteDate = useMemo(() => DateTime.fromISO(date), [date]);

  const {
    timesheetLabels: enabledLabels,
    loading: labelLoading,
    showStartEndTime,
    customLabels: enabledCustomLabels
  } = useLabels(noteDate);

  const labelsForNote = useMemo(
    () =>
      (note.labelValues ?? []).map((value) => {
        return mapTimesheetLabel(value.label, isSmartSuggestionEnabled);
      }),
    [note.labelValues, isSmartSuggestionEnabled]
  );

  const labels = useMemo(
    () => intersectionBy(labelsForNote, enabledLabels, 'id'),
    [enabledLabels, labelsForNote]
  );
  const timesheetLabels = useCollatedTimesheetLabels(note, labels);
  const customLabels = useCollatedCustomLabels(note, enabledCustomLabels);
  const formTimesheetLabels = useMemo(
    () => timesheetLabels.map((label) => ({ label })),
    [timesheetLabels]
  );

  const entryMsg = useMessage('note.splitEntryTitle');
  const [counter, setCounter] = useState(2);

  const defaultOffsetEntry = useMemo(
    () => ({
      id: OFFSET_ENTRY,
      date: DateTime.fromISO(date).toJSDate(),
      name: 'Offset',
      duration: 0,
      weight: 0,
      timesheetLabels: formTimesheetLabels,
      customLabels: customLabels
    }),
    [customLabels, date, formTimesheetLabels]
  );

  const defaultValues = useMemo<SplitNoteFormData>(() => {
    const duration = note.duration ? note.duration / 2 : 0;
    const startTime = note.startTime ? DateTime.fromISO(note.startTime) : null;
    const endTime = note.endTime ? DateTime.fromISO(note.endTime) : null;
    return {
      splitEntries: [
        {
          id: uuidv4(),
          name: `${entryMsg} 1`,
          date: DateTime.fromISO(date).toJSDate(),
          duration: duration,
          startTime: note.startTime
            ? DateTime.fromISO(note.startTime).toJSDate()
            : null,
          endTime: startTime?.plus({ seconds: duration }).toJSDate(),
          description: note.description ?? undefined,
          weight: 50,
          timesheetLabels: formTimesheetLabels,
          labelValuesInferenceId: note.labelValuesInferenceId
            ? `${note.labelValuesInferenceId}_0`
            : undefined,
          customLabels: customLabels
        },
        {
          id: uuidv4(),
          name: `${entryMsg} 2`,
          date: DateTime.fromISO(date).toJSDate(),
          duration: duration,
          startTime: endTime?.minus({ seconds: duration }).toJSDate(),
          endTime: note.endTime
            ? DateTime.fromISO(note.endTime).toJSDate()
            : null,
          description: note.description ?? undefined,
          weight: 50,
          timesheetLabels: formTimesheetLabels,
          labelValuesInferenceId: note.labelValuesInferenceId
            ? `${note.labelValuesInferenceId}_1`
            : undefined,
          customLabels: customLabels
        }
      ],
      classification: note.classification
    };
  }, [
    note.duration,
    note.startTime,
    note.endTime,
    note.description,
    note.classification,
    note.labelValuesInferenceId,
    entryMsg,
    date,
    formTimesheetLabels,
    customLabels
  ]);
  const isTiwDurationIndependentStartEndTime = useFeatureFlag(
    'isTiwDurationIndependentStartEndTime'
  );
  const formMethods = useForm<SplitNoteFormData>({
    defaultValues,
    resolver: yupResolver(
      isTiwDurationIndependentStartEndTime
        ? splitNoteSchema(true)
        : splitNoteSchema(false)
    ),
    shouldUnregister: false,
    mode: 'onChange'
  });
  const { control, setValue, getValues, reset } = formMethods;

  useEffect(() => {
    // if timesheet labels popuplated/changed after initial values (e.g. date changed)
    if (!labelLoading && formTimesheetLabels.length) {
      reset(
        {
          ...defaultValues,
          splitEntries: defaultValues.splitEntries.map((entry) => ({
            ...entry,
            timesheetLabels: formTimesheetLabels
          }))
        },
        {
          keepErrors: true, // errors will not be reset
          keepDirty: true // dirtyFields will not be reset
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelLoading]);

  const { fields } = useFieldArray({
    control,
    name: SPLIT_NOTE_FIELDS.SPLIT_ENTRIES,
    keyName: 'key'
  });

  const entries = getValues()?.splitEntries;
  const splitEntries = useMemo(() => entries ?? fields, [entries, fields]);
  const splitEntryHandler = useCallback(
    (id: string) => {
      const entries = splitEntries;
      const entryToSplitIndex = splitEntries.findIndex(
        (item) => item.id === id
      );
      const entryToSplit = entries[entryToSplitIndex];
      const duration = entryToSplit.duration ? entryToSplit.duration / 2 : 0;
      const weight = entryToSplit.weight ? entryToSplit.weight / 2 : 0;

      const startTime = entryToSplit.startTime
        ? DateTime.fromJSDate(entryToSplit.startTime)
        : null;
      const endTime = entryToSplit.endTime
        ? DateTime.fromJSDate(entryToSplit.endTime)
        : null;
      entries.splice(entryToSplitIndex, 1, {
        ...entryToSplit,
        startTime: entryToSplit.startTime,
        endTime: startTime?.plus({ seconds: duration }).toJSDate(),
        duration,
        weight,
        timesheetLabels: entryToSplit.timesheetLabels,
        customLabels: entryToSplit.customLabels
      });
      entries.splice(entryToSplitIndex + 1, 0, {
        id: uuidv4(),
        date: entryToSplit.date,
        name: `${entryMsg} ${counter + 1}`,
        startTime: endTime?.minus({ seconds: duration }).toJSDate(),
        endTime: entryToSplit.endTime,
        description: entryToSplit.description,
        duration,
        weight,
        timesheetLabels: entryToSplit.timesheetLabels?.map((value) => ({
          label: value.label
        })),
        labelValuesInferenceId: note.labelValuesInferenceId
          ? `${note.labelValuesInferenceId}_${counter}`
          : undefined,
        customLabels: entryToSplit.customLabels
      });

      setValue(`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`, entries, {
        shouldDirty: true
      });

      setCounter(1 + counter);
    },
    [splitEntries, entryMsg, counter, setValue, note.labelValuesInferenceId]
  );
  const removeSplitHandler = useCallback(
    (id: string) => {
      const entryToDeleteIndex = splitEntries.findIndex(
        (item) => item.id === id
      );
      splitEntries.splice(entryToDeleteIndex, 1);
      const hasOffetEntry =
        splitEntries[splitEntries.length - 1].id === OFFSET_ENTRY;
      const offsetEntry = hasOffetEntry
        ? splitEntries.pop() ?? defaultOffsetEntry
        : defaultOffsetEntry;
      const totalDuration = note.duration ?? 0;
      const actualTotalDuration = entries.reduce((total, entry) => {
        if (entry.id === OFFSET_ENTRY) {
          return total;
        }
        return total + (entry.duration ?? 0);
      }, 0);

      const recalculatedEntries = entries.map((entry) => {
        let weight = 0;
        if (totalDuration && entry.duration) {
          weight = (entry.duration / totalDuration) * 100;
        } else if (!entry.duration) {
          weight = 0;
        }
        return { ...entry, weight };
      });

      if (actualTotalDuration > totalDuration) {
        setValue(`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`, recalculatedEntries, {
          shouldDirty: true
        });
      } else {
        const offsetDuration = totalDuration - actualTotalDuration;
        setValue(
          `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`,
          [
            ...recalculatedEntries,
            {
              ...offsetEntry,
              startTime: undefined,
              endTime: undefined,
              weight: totalDuration
                ? (offsetDuration / totalDuration) * 100
                : 0,
              duration: offsetDuration
            }
          ],
          {
            shouldDirty: true
          }
        );
      }
    },
    [defaultOffsetEntry, entries, note.duration, setValue, splitEntries]
  );

  const fillOffsetHandler = useCallback(() => {
    const hasOffsetEntry =
      splitEntries[splitEntries.length - 1].id === OFFSET_ENTRY;
    if (hasOffsetEntry) {
      const offsetEntry = splitEntries[splitEntries.length - 1];
      setValue(
        `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`,
        [
          ...splitEntries.slice(0, -1),
          {
            ...offsetEntry,
            id: uuidv4(),
            name: `${entryMsg} ${counter + 1}`,
            newlyAddedOffsetEntry: true
          }
        ],
        {
          shouldDirty: true
        }
      );
      setCounter(1 + counter);
    }
  }, [counter, entryMsg, setValue, splitEntries]);

  const adjustEntriesHandler = useCallback(() => {
    const entries = getValues().splitEntries ?? splitEntries;

    const totalDuration = note.duration ?? 0;
    const actualTotalDuration = entries.reduce((total, entry) => {
      if (entry.id === OFFSET_ENTRY) {
        return total;
      }
      return total + (entry.duration ?? 0);
    }, 0);
    if (!totalDuration && !actualTotalDuration) {
      return;
    }
    const offsetDuration = totalDuration - actualTotalDuration;

    if (offsetDuration > 0) {
      const hasOffetEntry = entries[entries.length - 1].id === OFFSET_ENTRY;
      const offsetEntry = hasOffetEntry
        ? entries.pop() ?? defaultOffsetEntry
        : defaultOffsetEntry;

      const recalculatedEntries = entries.map((entry) => {
        let weight = 0;
        if (totalDuration && entry.duration) {
          weight = (entry.duration / totalDuration) * 100;
        } else if (!entry.duration) {
          weight = 0;
        }
        return { ...entry, weight };
      });

      setValue(
        `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`,
        [
          ...recalculatedEntries,
          {
            ...offsetEntry,
            weight: (offsetDuration / totalDuration) * 100,
            duration: offsetDuration
          }
        ],
        {
          shouldDirty: true
        }
      );
    } else {
      const hasOffetEntry = entries[entries.length - 1].id === OFFSET_ENTRY;
      if (hasOffetEntry) {
        entries.pop();
      }
      const recalculatedEntries = entries.map((entry) => {
        let weight = 0;
        if (actualTotalDuration && entry.duration) {
          weight = (entry.duration / actualTotalDuration) * 100;
        } else if (!entry.duration) {
          weight = 0;
        }
        return { ...entry, weight };
      });
      setValue(`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`, [...recalculatedEntries]);
    }
  }, [defaultOffsetEntry, getValues, note.duration, setValue, splitEntries]);

  return {
    formMethods,
    splitEntries: splitEntries as SplitEntryFormData[],
    splitEntryHandler,
    removeSplitHandler,
    fillOffsetHandler,
    adjustEntriesHandler,
    labelLoading,
    showStartEndTime
  };
};
